import { ChangeDetectorRef, Component, ErrorHandler, Injector, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { FieldTree, form, required, validate } from '@angular/forms/signals'
import { ActivatedRoute, Router } from '@angular/router'

import { MatInput } from '@angular/material/input'
import { MatSnackBar } from '@angular/material/snack-bar'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { FieldErrors, FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { Maa, MaaService } from '../_jaettu-angular/service/maa.service'

import { BicService } from '@lemon/core/service/bic.service'
import { DateService } from '@lemon/core/service/date.service'
import { IbanService } from '@lemon/core/service/iban.service'
import { ViitenumeroService } from '@lemon/core/service/viitenumero.service'
import { AlvIlmoitusjakso, Asiakas, AsiakkaanSopimusversio } from '../_jaettu-lemonator/model/asiakas'

import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { StringService } from '@lemon/core/service/string.service'
import { AsiakasKopioija } from '../_angular/service/asiakas/asiakas.kopioija'
import { AsiakasService, LokalisoituAlvIlmoitusjakso, LokalisoituYritysmuoto } from '../_angular/service/asiakas/asiakas.service'
import { KirjanpitajanTiedot, KirjautunutKayttajaService } from '../_angular/service/kirjautunut-kayttaja.service'

import { KirjanpidonPeruste, SopimusPalvelupaketti, Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { ReplaySubject, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { KirjanpitajanTiimiService } from '../_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { SopimusversioLomake } from '../asiakkaat/asetukset/sopimusversio.component'

interface AsiakasLisaaLomake {
  nimi: string
  ytunnus: string
  katuosoite: string
  maa: string | null
  postinro: string
  postitoimipaikka: string
  yritysmuoto: Yritysmuoto | null
  alvIlmoitusjakso: AlvIlmoitusjakso | null
  tilikausiAlkaa: Date | null
  tilikausiLoppuu: Date | null
  iban: string
  lisaavastaanottaja: string
  voimassaAlkaen: Date | null
  hinta: number | null
  hinnanPerusteet: string
  viitenumero: string | null
  saleDoneBy: string | null
}

@Component({
  templateUrl: './asiakas-lisaa.component.html',
  styleUrls: ['./asiakas-lisaa.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakasLisaaComponent implements OnInit, OnDestroy {
  @ViewChild(MatInput, { static: true }) nimiInput: MatInput

  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  suodatetutMaat: ReplaySubject<Maa[]> = new ReplaySubject(1)
  private readonly _injector = inject(Injector)
  private readonly _vastaanottajaPuuttuu = signal(false)

  lomake: FieldTree<AsiakasLisaaLomake>
  ytunnusKaytossa = signal<{ asiakasAvain: string; nimi: string } | null>(null)
  sopimusversioLomake: FieldTree<SopimusversioLomake>
  fieldErrors: FieldErrors = {}
  paivitaValidaatiot: any = null
  minMonth = new Date()
  asiakas: Asiakas = null

  yritysmuodot: LokalisoituYritysmuoto[] = null
  alvIlmoitusjaksot: LokalisoituAlvIlmoitusjakso[] = null
  ytunnusVirhe: boolean = false

  salesTeamOptions = this._kirjanpitajanTiimiService.getHardcodedSalesTeamOptions().filter(option => option.aktiivinen)

  private _saveStarted: boolean

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router,
    private _route: ActivatedRoute,
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _asiakasKopioija: AsiakasKopioija,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _snackbar: MatSnackBar,
    private _maaService: MaaService,
    private _dateService: DateService,
    private _ibanService: IbanService,
    private _bicService: BicService,
    private _viitenumeroService: ViitenumeroService,
    private _timestampService: TimestampService,
    private _codeCheckService: CodeCheckService,
    private _stringService: StringService,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
  ) {
    this.yritysmuodot = this._asiakasService.yritysmuodot
    this.alvIlmoitusjaksot = this._asiakasService.alvIlmoitusjaksot

    const d = new Date()
    d.setDate(1)
    this.minMonth = this._dateService.lisaaKuukausia(d, 1)

    this.asiakas = this._asiakasKopioija.annaUusiAsiakas()
    this.asiakas.hinnat = []
    this.asiakas.hinnat.push({
      hinta: null,
      voimassaAlkaen: this._dateService.dateToLocalDate(this.minMonth),
      muutoksenSyy: 'Aloitushinta',
      muutosPvm: this._timestampService.now(),
    })
    this.asiakas.tilikaudet = []
    this.asiakas.tilikaudet.push({
      avain: this._firebase.firestoreCreateId(),
      alkaa: null,
      loppuu: null,
      tilikartanProfiili: 'maksuperusteinen',
    })
    this.asiakas.tilinumerot = []
    this.asiakas.tilinumerot.push({
      iban: null,
    })
    this.asiakas.alvIlmoitusjaksot = []
    this.asiakas.alvIlmoitusjaksot.push({
      alvIlmoitusjakso: null,
      muutosPvm: this._timestampService.now(),
      voimassaAlkaen: { year: 1980, month: 1, day: 1 },
    })

    const sopimuskausiAlkaa = this._dateService.currentLocalDate()
    const sopimusversioAvain = this._firebase.firestoreCreateId()
    this.asiakas.sopimuskaudet = []
    this.asiakas.sopimuskaudet.push({
      avain: this._firebase.firestoreCreateId(),
      alkaa: sopimuskausiAlkaa,
      lisatiedot: null,
      lopettamisenSyy: null,
      lahetaPaattymassaEmail: null,
      lahetaPaattynytEmail: null,
      loppuu: null,
      tositteidenToimitusLoppuu: null,
      tauot: null,
      luoja: null,
      luotu: null,
      paivitetty: null,
      paivittaja: null,
      poistettu: false,
      sopimusversiot: [
        {
          avain: sopimusversioAvain,
          palvelupaketti: null,
          lisapalvelut: {},
          luoja: null,
          luotu: null,
          voimassaAlkaen: this._dateService.localDateToNumber(sopimuskausiAlkaa),
          allekirjoitusStatus: 'ei-allekirjoitettu',
        },
      ],
      uusinSopimusversio: sopimusversioAvain,
    })
    this.asiakas.uudessaListassa = true
    this.asiakas.uudessaKirjanpitoAppissaAlkaen = this._dateService.localMonthToNumber(this._dateService.currentLocalMonth())
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  private minimiKuukausValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (control.value) {
      const minDate = new Date()
      if (this._dateService.kuukausiaValissa(control.value, minDate) < 0) {
        return { min: true }
      }
    }
    return null
  }

  private validateEmail: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    return Validators.email(ctrl)
  }

  private validateViitenumero: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    if (!this._viitenumeroService.onkoViitenumeroValidi(ctrl.value)) {
      return { viitenumero: true }
    }
    return null
  }

  ngOnInit() {
    this.lomake = form(
      signal<AsiakasLisaaLomake>({
        nimi: '',
        ytunnus: '',
        katuosoite: '',
        maa: this.asiakas.maa ?? null,
        postinro: '',
        postitoimipaikka: '',
        yritysmuoto: null,
        alvIlmoitusjakso: this.asiakas.alvIlmoitusjaksot[0].alvIlmoitusjakso ?? null,
        tilikausiAlkaa: null,
        tilikausiLoppuu: null,
        iban: '',
        lisaavastaanottaja: '',
        voimassaAlkaen: this.minMonth,
        hinta: null,
        hinnanPerusteet: '',
        viitenumero: null,
        saleDoneBy: null,
      }),
      polku => {
        required(polku.nimi)
        required(polku.ytunnus)
        validate(polku.ytunnus, () => (this.ytunnusKaytossa() ? { kind: 'tupla' } : null))
        required(polku.yritysmuoto)
        required(polku.alvIlmoitusjakso)
        validoiReaktiivisesti(polku.iban, this.ibanValidator)
        validoiReaktiivisesti(polku.lisaavastaanottaja, this.validateEmail, { when: () => !this._vastaanottajaPuuttuu() })
        validate(polku.lisaavastaanottaja, () => (this._vastaanottajaPuuttuu() ? { kind: 'min' } : null))
        required(polku.voimassaAlkaen)
        validoiReaktiivisesti(polku.voimassaAlkaen, this.minimiKuukausValidator)
        required(polku.hinta)
        validoiReaktiivisesti(polku.hinta, Validators.min(0.0))
        validoiReaktiivisesti(polku.hinta, Validators.max(2000))
        validoiReaktiivisesti(polku.viitenumero, this.validateViitenumero)
        required(polku.saleDoneBy)
      },
      { injector: this._injector },
    )
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    // Bind to changes
    // Yhteystiedot
    muutokset(this.lomake.nimi)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.nimi = this._validationService.processValue(value)
      })
    muutokset(this.lomake.ytunnus)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.ytunnusKaytossa.set(null)
        this.asiakas.ytunnus = this._validationService.processValue(value)
        if (this.asiakas.ytunnus && !this._codeCheckService.isValidYTunnus(this.asiakas.ytunnus)) {
          this.ytunnusVirhe = true
        } else {
          this.ytunnusVirhe = false
        }
      })
    muutokset(this.lomake.katuosoite)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.katuosoite = this._validationService.processValue(value)
      })
    muutokset(this.lomake.maa)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.maa = this._validationService.processValue(value)
      })
    muutokset(this.lomake.postinro)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.postinro = this._validationService.processValue(value)
      })
    muutokset(this.lomake.postitoimipaikka)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.postitmp = this._validationService.processValue(value)
      })

    // Kirjanpito
    muutokset(this.lomake.yritysmuoto)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.yritysmuoto = this._validationService.processValue(value)
      })
    muutokset(this.lomake.alvIlmoitusjakso)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.alvIlmoitusjaksot[0].alvIlmoitusjakso = this._validationService.processValue(value)
      })
    // this.form.get('tilikausi').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(value => { this.asiakas.tilikaudet[0].kausi = this.validationService.processValue(value) })
    muutokset(this.lomake.tilikausiAlkaa)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((value: Date) => {
        this.asiakas.tilikaudet[0].alkaa = this._dateService.dateToLocalDate(value)
      })
    muutokset(this.lomake.tilikausiLoppuu)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((value: Date) => {
        this.asiakas.tilikaudet[0].loppuu = this._dateService.dateToLocalDate(value)
      })
    // Pankkiyhteys
    // this.form.get('pankki').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(value => { this.asiakas.tilinumerot[0].pankki = this.validationService.processValue(value) })
    muutokset(this.lomake.iban)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        const iban = this._stringService.removeAllWhiteSpaces(this._validationService.processValue(value))
        this.asiakas.tilinumerot[0].iban = iban

        // const pankinNimi = this.bicService.annaPankinNimi(iban)
        // const bic = this.bicService.annaBicKoodi(iban)
        // if (pankinNimi) {
        //   this.form.get('pankki').setValue(pankinNimi)
        // }
        // if (bic) {
        //   this.form.get('bic').setValue(bic)
        // }
      })
    // this.form.get('bic').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(value =>    { this.asiakas.tilinumerot[0].bic    = this.validationService.processValue(value) })

    // Laskutus
    muutokset(this.lomake.voimassaAlkaen)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((value: Date) => {
        this.asiakas.hinnat[0].voimassaAlkaen = this._dateService.dateToLocalDate(value)
      })
    muutokset(this.lomake.hinta)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.hinnat[0].hinta = value
      })
    muutokset(this.lomake.hinnanPerusteet)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.hinnanPerusteet = this._validationService.processValue(value)
      })
    muutokset(this.lomake.viitenumero)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.viitenumero = this._validationService.processValue(value)
      })
    muutokset(this.lomake.lisaavastaanottaja)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((value: string) => {
        this._vastaanottajaPuuttuu.set(false)
        // console.log('suora', value)
        if (value && (value.indexOf(',') !== -1 || value.indexOf(' ') !== -1)) {
          this.lisaaEmail(this.lomake.lisaavastaanottaja)
        }
      })

    muutokset(this.lomake.saleDoneBy)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asiakas.saleDoneBy = value
      })

    this._maaService.kaikkiMaatObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(maat => {
      this.suodatetutMaat.next(maat)
    })

    this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kirjanpitajanTiedot => {
      const kirjanpitajanAvain = this._getKirjanpitajanAvain(kirjanpitajanTiedot)

      this.asiakas.kasittelija = kirjanpitajanAvain
      this.asiakas.kirjanpitajat = []

      const nyt = this._dateService.dateToLocalDate(new Date())
      nyt.day = 1

      this.asiakas.kirjanpitajat.push({
        kirjanpitajanAvain: kirjanpitajanAvain,
        voimassaoloAlkaa: nyt,
      })

      if (this._onkoMyyntitiimissa(kirjanpitajanTiedot)) {
        this.asiakas.siirrettyJakolistalta = {
          salesAlkaa: this._dateService.dateToLocalDate(new Date()),
        }
      }
      setTimeout(() => {
        this.nimiInput.focus()
        this._changeDetectorRef.markForCheck()
      }, 10)
    })

    this.sopimusversioLomake = this._luoSopimusversioLomake(this.asiakas.sopimuskaudet[0].sopimusversiot[0])
  }

  private _getKirjanpitajanAvain(kirjanpitajanTiedot: KirjanpitajanTiedot): string {
    // Annika / Isabel / Heidi K.
    // { avain: '259AJG2HoJctamvUlUYHIC1uonU2', etunimi: 'Annika', sukunimi: 'Toivonen' },
    // { avain: 'xm5x1jlOjaNoOnOOkHyKdmoU2VG3', etunimi: 'Isabel', sukunimi: 'Rikander' },
    // { avain: 'gAV0KTVarYg8GauChfePM3VCFfO2', etunimi: 'Heidi', sukunimi: 'Kuisma' },
    if (kirjanpitajanTiedot.uid === '259AJG2HoJctamvUlUYHIC1uonU2' || kirjanpitajanTiedot.uid === 'xm5x1jlOjaNoOnOOkHyKdmoU2VG3' || kirjanpitajanTiedot.uid === 'gAV0KTVarYg8GauChfePM3VCFfO2') {
      return 'QgPvtcCjoOdf6Zg7lgMwqLWp2BG2' // Add customers created straight to Holvi list
    }

    if (this._onkoMyyntitiimissa(kirjanpitajanTiedot)) {
      return 't86aMdfTkicMvOiKGGjKm4m3Rwm1' // sales@lemontree.fi
    }

    return kirjanpitajanTiedot.uid // otherwise keep creator UID
  }

  private _onkoMyyntitiimissa(kirjanpitajanTiedot: KirjanpitajanTiedot): boolean {
    return this._myynninTekijatUids.has(kirjanpitajanTiedot.uid)
  }

  // Sales (Sanna / Mikaela / Petra / Hanna), goes to sales list.
  private readonly _myynninTekijatUids = new Set([
    'QL4CBlJTLdQLtbb4k8AURFYeg3I3', // Sanna Vihersaari
    'qR99vZ0JoTfthtSpB7GLp89zjb63', // Mikaela Gardemeister
    'hZo5I0KWkrfs7eoIpAhx4foqXCC2', // Petra Pasula
    'tst8k0sa81QUu2OrOtHiBPAbPZh1', // Hanna Räsänen
  ])

  private ibanValidator: ValidatorFn = (c: AbstractControl): ValidationErrors | null => {
    // console.log(c.value, this.ibanService.isValidIban(c.value))
    if (c && c.value && !this._ibanService.isValidIban(c.value)) {
      return { iban: true }
    }
    return null
  }

  // private bicValidator: ValidatorFn = (c: AbstractControl): ValidationErrors | null => {
  //   // console.log(c.value, this.ibanService.isValidIban(c.value))
  //   if (c && c.value && !this.bicService.validoiBic(c.value)) {
  //     return { 'bic': true }
  //   }
  //   return null
  // }

  async save() {
    if (!this.asiakas || !this.asiakas.laskunVastaanottajat || this.asiakas.laskunVastaanottajat.length < 1) {
      this._vastaanottajaPuuttuu.set(true)
    }

    if (!this.lomake().valid() || !this.sopimusversioLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      this._validationService.merkitseKokoLomakeKosketuksi(this.sopimusversioLomake)
      this.paivitaValidaatiot = {}
      return
    }

    if (this._saveStarted) {
      return
    }
    this._saveStarted = true
    this._ladataanService.aloitaLataaminen()

    try {
      const olemassaOleva = await this._asiakasService.annaAsiakkaatYtunnuksella(this.asiakas.ytunnus).then(vastaus => {
        for (const asiakas of vastaus?.loytyneet || []) {
          if (asiakas.asiakasAvain !== this.asiakas.avain) {
            return asiakas
          }
        }
        return null
      })

      if (olemassaOleva) {
        this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
        this.ytunnusKaytossa.set(olemassaOleva)

        this._ladataanService.lopetaLataaminen()
        this._saveStarted = false
        return
      }

      const tilinumero = this.asiakas.tilinumerot[0]
      if (!tilinumero || !tilinumero.iban || !tilinumero.iban.trim()) {
        this.asiakas.tilinumerot = []
      }

      const tilikausi = this.asiakas.tilikaudet[0]
      if (!tilikausi || !tilikausi.alkaa || !tilikausi.loppuu) {
        this.asiakas.tilikaudet = []
      }

      this.asiakas.kirjanpidonPerusteet = [
        {
          alkaa: null,
          loppuu: null,
          peruste: KirjanpidonPeruste.MAKSU,
        },
      ]

      const kirjanpitajanTiedot = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()

      this.asiakas.sopimuskaudet[0].luoja = kirjanpitajanTiedot.uid
      this.asiakas.sopimuskaudet[0].paivittaja = kirjanpitajanTiedot.uid
      this.asiakas.sopimuskaudet[0].luotu = this._timestampService.now()
      this.asiakas.sopimuskaudet[0].paivitetty = this.asiakas.sopimuskaudet[0].luotu

      this.asiakas.sopimuskaudet[0].sopimusversiot[0].luoja = kirjanpitajanTiedot.uid
      this.asiakas.sopimuskaudet[0].sopimusversiot[0].luotu = this._timestampService.now()

      // Holvi Zen -asiakkailla ei ole Lemonaid-sopimusta, joten sopimusversio merkitään suoraan allekirjoitetuksi.
      if (this.asiakas.sopimuskaudet[0].sopimusversiot[0].palvelupaketti === SopimusPalvelupaketti.HOLVI_ZEN) {
        this.asiakas.sopimuskaudet[0].sopimusversiot[0].allekirjoitusStatus = 'allekirjoitettu'
      }

      await this._asiakasService.paivitaAsiakas(this.asiakas, 'paivita-tilikausiin-perustuvat-tiedot', true, false)
      // This must be a separate call due to the way security rules currently work.
      let maksutavatSaveFailed = false
      await this._asiakasService.lisaaDefaultMaksutavatAsiakkaalle(this.asiakas).catch(err => {
        this._errorHandler.handleError(err)
        maksutavatSaveFailed = true
      })
      this._asiakasService.asetaNykyinenAsiakas(this.asiakas)
      this._ladataanService.lopetaLataaminen()
      this._saveStarted = false
      this._router.navigate(['asiakkaat', this.asiakas.avain, 'asiakastiedot'])
      if (!maksutavatSaveFailed) {
        this._snackbar.open('Asiakas tallennettiin onnistuneesti.', 'OK', { duration: 5000, verticalPosition: 'top' })
      } else {
        this._snackbar.open('Asiakas tallennettiin onnistuneesti, mutta oletusmaksutapojen lisääminen epäonnistui.', 'OK', { duration: 5000, verticalPosition: 'top' })
      }
    } catch (err) {
      this._ladataanService.lopetaLataaminen()
      this._saveStarted = false
      this._errorHandler.handleError(err)
    }
  }

  pankkiJaIbanTeksti(): string {
    const kentta = this.lomake.iban()

    if (kentta.valid() && kentta.value()) {
      const iban = kentta.value()
      // console.log(iban)
      const pankinNimi = this._bicService.annaPankinNimi(iban)
      const bic = this._bicService.annaBicKoodi(iban)
      if (pankinNimi || bic) {
        let palautettava = ''
        palautettava += pankinNimi ? pankinNimi : 'Pankin nimeä ei pystytty tunnistamaan.'
        palautettava += bic ? ' (' + bic + ')' : ' BIC-koodia ei pystytty tunnistamaan.'
        return palautettava
      } else {
        return 'Pankkia tai BIC koodia ei pystytty tunnistamaan IBAN:ista'
      }
    }
    return ''
  }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string) {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }

  name(): string {
    return '' + Math.random()
  }

  lisaaLaskunVastaanottaja() {
    this._vastaanottajaPuuttuu.set(false)
    this.lisaaEmail(this.lomake.lisaavastaanottaja)
  }

  private lisaaEmail(field: FieldTree<string>) {
    const value = field().value()
    // console.log('raaka', field.value)
    if ((value || '').trim()) {
      let arvo = value.trim()
      arvo = arvo.replace(/,/g, '').trim()
      // console.log('kasitelty', arvo)
      if (!Validators.email({ value: arvo } as AbstractControl)) {
        this.asiakas.laskunVastaanottajat.push(arvo)
        asetaArvo(field, '')
      }
    }
  }

  poistaLaskunVastaanottaja(vastaanottaja: string): void {
    const index = this.asiakas.laskunVastaanottajat.indexOf(vastaanottaja)
    if (index >= 0) {
      this.asiakas.laskunVastaanottajat.splice(index, 1)
    }
  }

  private _luoSopimusversioLomake(sopimusversio: AsiakkaanSopimusversio): FieldTree<SopimusversioLomake> {
    const sopimusversioLomake = form(
      signal<SopimusversioLomake>({ palvelupaketti: null, palkat: null, sopimusversioVoimassaAlkaen: null }),
      polku => {
        required(polku.palvelupaketti)
      },
      { injector: this._injector },
    )
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this._ngUnsubscribe))

    muutokset(sopimusversioLomake.palvelupaketti).subscribe(val => {
      sopimusversio.palvelupaketti = val
    })
    muutokset(sopimusversioLomake.palkat).subscribe(val => {
      sopimusversio.lisapalvelut.PALKAT = val
    })
    // this.sopimusversioForm.get('ennakkoverojenSeuranta').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //  sopimusversio.lisapalvelut.ENNAKKOVEROJEN_SEURANTA = val
    // })
    // this.sopimusversioForm.get('ostolaskujenMaksaminen').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   sopimusversio.lisapalvelut.OSTOLASKUJEN_MAKSAMINEN = val
    // })
    // this.sopimusversioForm.get('kirjanpidonReskontra').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   sopimusversio.lisapalvelut.KIRJANPIDON_RESKONTRA = val
    // })
    // this.sopimusversioForm.get('myyntilaskujenLahettaminen').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   sopimusversio.lisapalvelut.MYYNTILASKUJEN_LAHETTAMINEN = val
    // })
    // this.sopimusversioForm.get('reskontranHoito').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   sopimusversio.lisapalvelut.RESKONTRAN_HOITO = val
    // })
    // this.sopimusversioForm.get('saatavienValvonta').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   this._sopimuskausiVersio.lisapalvelut.SAATAVIEN_VALVONTA = val
    // })

    muutokset(sopimusversioLomake.sopimusversioVoimassaAlkaen).subscribe(val => {
      if (val) {
        sopimusversio.voimassaAlkaen = this._dateService.dateToNumber(val)
      }
    })

    return sopimusversioLomake
  }
}
